'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

function monthlyPayment(principal: number, annualRatePct: number, termYears: number) {
  const months = termYears * 12;
  if (months <= 0) return { payment: 0, totalPaid: 0, totalInterest: 0 };
  const r = annualRatePct / 100 / 12;
  const payment =
    r === 0 ? principal / months : (principal * r * (1 + r) ** months) / ((1 + r) ** months - 1);
  const totalPaid = payment * months;
  const totalInterest = totalPaid - principal;
  return { payment, totalPaid, totalInterest };
}

interface LoanInputs {
  principal: number;
  rate: number;
  years: number;
}

function LoanFields({
  label,
  loan,
  onChange,
}: {
  label: string;
  loan: LoanInputs;
  onChange: (loan: LoanInputs) => void;
}) {
  return (
    <div className="quiz-question">
      <legend style={{ fontWeight: 700, marginBottom: '0.4rem' }}>{label}</legend>
      <div
        style={{
          display: 'grid',
          gap: '0.6rem',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        }}
      >
        <label className="tool-live-field">
          <span>Loan amount</span>
          <input
            min={0}
            onChange={(e) => onChange({ ...loan, principal: Number(e.target.value) || 0 })}
            type="number"
            value={loan.principal}
          />
        </label>
        <label className="tool-live-field">
          <span>Interest rate (% APR)</span>
          <input
            onChange={(e) => onChange({ ...loan, rate: Number(e.target.value) || 0 })}
            step={0.01}
            type="number"
            value={loan.rate}
          />
        </label>
        <label className="tool-live-field">
          <span>Term (years)</span>
          <input
            min={0}
            onChange={(e) => onChange({ ...loan, years: Number(e.target.value) || 0 })}
            type="number"
            value={loan.years}
          />
        </label>
      </div>
    </div>
  );
}

export default function BrowserLoanComparisonCalculator() {
  const fired = useRef(false);
  const fireUsage = () => {
    if (fired.current) return;
    fired.current = true;
    void consumeToolUsage('loan-comparison-calculator', '').catch(() => undefined);
  };

  const [loanA, setLoanA] = useState<LoanInputs>({ principal: 300000, rate: 6.5, years: 30 });
  const [loanB, setLoanB] = useState<LoanInputs>({ principal: 300000, rate: 6.0, years: 15 });

  function withUsage(setter: (l: LoanInputs) => void) {
    return (l: LoanInputs) => {
      fireUsage();
      setter(l);
    };
  }

  const resultA = useMemo(() => monthlyPayment(loanA.principal, loanA.rate, loanA.years), [loanA]);
  const resultB = useMemo(() => monthlyPayment(loanB.principal, loanB.rate, loanB.years), [loanB]);

  const cheaperTotal = resultA.totalPaid <= resultB.totalPaid ? 'A' : 'B';

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Finance</span>
        <h2 className="tool-live-title">Loan comparison calculator</h2>
        <p className="tool-live-subtitle">
          Enter 2 loan offers side by side to compare the monthly payment, total interest, and total
          cost.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader" style={{ display: 'grid', gap: '0.9rem' }}>
          <LoanFields label="Loan A" loan={loanA} onChange={withUsage(setLoanA)} />
          <LoanFields label="Loan B" loan={loanB} onChange={withUsage(setLoanB)} />
        </section>

        <section className="tool-live-preview">
          <p className="tool-live-panel-note">
            <strong>
              Loan A: {resultA.payment.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              /mo
            </strong>
            <span>
              Total paid {resultA.totalPaid.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              , total interest{' '}
              {resultA.totalInterest.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </p>
          <p className="tool-live-panel-note">
            <strong>
              Loan B: {resultB.payment.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              /mo
            </strong>
            <span>
              Total paid {resultB.totalPaid.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              , total interest{' '}
              {resultB.totalInterest.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </span>
          </p>
          <p className="tool-live-panel-note">
            <strong style={{ color: '#1baf7a' }}>Loan {cheaperTotal} costs less overall</strong>
            <span>
              by{' '}
              {Math.abs(resultA.totalPaid - resultB.totalPaid).toLocaleString(undefined, {
                maximumFractionDigits: 0,
              })}{' '}
              over the life of the loan, even if its monthly payment is higher.
            </span>
          </p>
        </section>
      </div>
    </div>
  );
}
